// A Multi-select custom field's popover body on the Opportunities list: Select all over a checkbox per option,
// in the definition's order. Ticking saves nothing yet: the selection is saved once, when the popover closes
// (Escape, a click outside it), and only if it differs from the stored one. The popover's body unmounts as it
// closes, so that is when it saves. An empty selection clears the value. Select all ticks every option the
// field has now, and clears them all when every one is ticked; an option added later is never ticked on its
// own. No search, no limit.
import React, {useEffect} from 'react';
import Styled from 'styled-components';
import {Checkbox, Separator, tokensForThemeProvider} from '@forecast-it/design-system';
import {chosenKeys, useMultiSelectSelection} from '../../../../../components/useMultiSelectSelection';
import {saveCustomFieldValue} from './saveCustomFieldValue';
import type {CustomFieldEditorProps} from './CustomFieldEditor';

const tokens = tokensForThemeProvider.sky.sys;

// The same frame as the dropdown editor's list (editorOptionList), one checkbox to a row.
const List = Styled.div`
	display: flex;
	flex-direction: column;
	gap: ${tokens.spacing['300']};
	width: 280px;
	max-height: 320px;
	overflow-y: auto;
	padding: ${tokens.spacing['400']};
`;
// Keeps the design-system separator between Select all and the options from shrinking in the flex column.
const SelectAllSeparator = Styled.div`
	flex: none;
`;

export const MultiSelectFieldEditor = ({node, def, current, onCommit, labels}: CustomFieldEditorProps): JSX.Element => {
	const keys = def.options.map(option => option.key);
	const {chosen, selectAll, toggle, toggleAll, changedFrom, latestValue} = useMultiSelectSelection(current, keys);
	useEffect(
		() => () => {
			// The save runs as the editor unmounts, after its last render; the hook reads the latest ticks.
			if (changedFrom(chosenKeys(current, keys))) {
				saveCustomFieldValue({node, def, value: latestValue(), onCommit});
			}
		},
		// Once, as the editor closes, against the value and options it opened with.
		[]
	);

	return (
		<List data-cy="cf-multi-select-editor">
			{keys.length > 0 ? (
				<>
					<Checkbox
						checked={selectAll}
						onChange={toggleAll}
						labelText={labels.selectAll}
						aria-label={labels.selectAll}
						data-cy="cf-multi-select-all"
					/>
					<SelectAllSeparator>
						<Separator />
					</SelectAllSeparator>
				</>
			) : null}
			{def.options.map(option => (
				<Checkbox
					key={option.id}
					checked={chosen.includes(option.key)}
					onChange={() => toggle(option.key)}
					labelText={option.displayName}
					notBoldLabel
					aria-label={option.displayName}
					data-cy={`cf-multi-select-option-${option.key}`}
				/>
			))}
		</List>
	);
};

export default MultiSelectFieldEditor;
